import { Component, Input, OnInit } from '@angular/core';
import { ModalController, AlertController } from '@ionic/angular';
import { Trabajo } from '../../models/trabajo';
import { JavapiService } from '../../services/javapi.service';
import { MetodosgenService } from '../../services/metodosgen.service';
import { Metodosui } from '../../services/metodosui';
import { firstValueFrom } from 'rxjs';
import { WorkerSelectorComponent } from '../worker-selector/worker-selector.component';
import { EditActivityComponent } from '../edit-activity/edit-activity.component';

@Component({
  selector: 'app-planilla-calendar',
  templateUrl: './planilla-calendar.component.html',
  styleUrls: ['./planilla-calendar.component.scss'],
  standalone: false
})
export class PlanillaCalendarComponent implements OnInit {

  @Input() trabajo: Trabajo | null = null;

  selectedDate: string = '';
  dailyWorkers: any[] = [];
  loading: boolean = false;
  highlightedDates: any[] = [];
  hasChanges: boolean = false;
  filterOnlyRegistered: boolean = false;

  /** Lunes de la semana actualmente visible */
  weekStart: Date = this.getMondayOfDate(new Date());

  /** Array de 7 fechas (Lun → Dom) de la semana visible */
  get weekDays(): Date[] {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(this.weekStart);
      d.setDate(d.getDate() + i);
      return d;
    });
  }

  /** Nombres cortos de los días, empezando en lunes */
  readonly DAY_NAMES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  /** Título de la semana en formato legible */
  get weekLabel(): string {
    const end = new Date(this.weekStart);
    end.setDate(end.getDate() + 6);
    const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
    const locale = 'es-ES';
    return `${this.weekStart.toLocaleDateString(locale, opts)} – ${end.toLocaleDateString(locale, opts)}`;
  }

  constructor(
    private modalCtrl: ModalController,
    private javapiService: JavapiService,
    private metodosGenService: MetodosgenService,
    private metodosUi: Metodosui,
    private alertCtrl: AlertController
  ) { }

  // ─── Helpers de semana ───────────────────────────────────────────

  /**
   * Devuelve el lunes de la semana a la que pertenece una fecha dada.
   * Respeta zonas horarias locales para evitar el bug de UTC-offset.
   */
  getMondayOfDate(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay(); // 0=Dom, 1=Lun … 6=Sáb
    const diff = day === 0 ? -6 : 1 - day; // ajuste para que Lun=0
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  /** Formatea una Date como 'YYYY-MM-DD' local (sin UTC shift) */
  toLocalISO(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  isToday(date: Date): boolean {
    return this.toLocalISO(date) === this.toLocalISO(new Date());
  }

  isSelected(date: Date): boolean {
    return this.toLocalISO(date) === this.selectedDate;
  }

  isHighlighted(date: Date): boolean {
    const iso = this.toLocalISO(date);
    return this.highlightedDates.some(h => h.date === iso);
  }

  selectDay(date: Date) {
    this.selectedDate = this.toLocalISO(date);
    this.loadDailyActivity();
  }

  prevWeek() {
    const d = new Date(this.weekStart);
    d.setDate(d.getDate() - 7);
    this.weekStart = d;
  }

  nextWeek() {
    const d = new Date(this.weekStart);
    d.setDate(d.getDate() + 7);
    this.weekStart = d;
  }

  async loadHighlightedDates() {
    if (!this.trabajo) return;
    try {
      const dates: any = await firstValueFrom(this.javapiService.getDiasTrabajados(String(this.trabajo.id)));
      if (Array.isArray(dates)) {
        this.highlightedDates = dates.map(dateStr => ({
          date: dateStr,
          textColor: '#764ba2',
          backgroundColor: '#f1eff9'
        }));
      }
    } catch (e) {
      console.error('Error loading worked days', e);
    }
  }

  ngOnInit() {
    this.selectedDate = this.toLocalISO(new Date());
    this.loadDailyActivity();
    this.loadHighlightedDates(); // Load highlights
  }

  toggleFilter() {
    this.filterOnlyRegistered = !this.filterOnlyRegistered;
    if (!this.filterOnlyRegistered) {
      const today = new Date();
      this.weekStart = this.getMondayOfDate(today);
      this.selectedDate = this.toLocalISO(today);
      this.loadDailyActivity();
    }
  }

  formatPillDate(dateStr: string): string {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    const opts: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' };
    return date.toLocaleDateString('es-ES', opts);
  }

  selectPillDate(dateStr: string) {
    this.selectedDate = dateStr;
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const targetDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      this.weekStart = this.getMondayOfDate(targetDate);
    }
    this.loadDailyActivity();
  }

  close() {
    this.modalCtrl.dismiss({ updated: this.hasChanges });
  }

  onDateChange(event: any) {
    this.selectedDate = event.detail.value.split('T')[0];
    this.loadDailyActivity();
  }

  async loadDailyActivity() {
    if (!this.trabajo) return;
    this.loading = true;
    try {
      const user = localStorage.getItem('user_name') || 'system'; // Get user from local storage
      const data: any = await firstValueFrom(
        this.javapiService.getPlanillaActividad(String(this.trabajo.id), this.selectedDate, user)
      );

      this.dailyWorkers = [];
      if (Array.isArray(data)) {
        this.dailyWorkers = data.map(d => ({
          id: d.dt1,
          trabajo_id: d.dt2,
          trabajador_id: d.dt3,
          fecha: d.dt4,
          turno: d.dt5,
          monto: Number(d.dt6),
          extra: Number(d.dt7),
          descuento: Number(d.dt8),
          subcontrato: d.dt9 === 'S',
          comentario: d.dt10,
          nombre: d.dt12,
          alias: d.dt13,
          cedula: d.dt14,
          rol: d.dt15,
          costo_dia: Number(d.dt16) || 0,
          costo_noche: Number(d.dt17) || 0
        })).sort((a, b) => Number(b.id) - Number(a.id)); // Newest first
      }
    } catch (e) {
      console.error('Error loading daily activity', e);
    } finally {
      this.loading = false;
    }
  }

  async addWorker() {
    // Extract workers already added with their turn for this day/job
    const existingWorkers = this.dailyWorkers.map(w => ({
      trabajador_id: w.trabajador_id,
      turno: w.turno
    }));

    const modal = await this.modalCtrl.create({
      component: WorkerSelectorComponent,
      componentProps: {
        trabajo: this.trabajo,
        date: this.selectedDate,
        existingIds: this.dailyWorkers.map(w => w.trabajador_id), // Mantener por compatibilidad
        existingWorkers: existingWorkers // Pasar lista con turno
      }
    });

    await modal.present();
    const { data } = await modal.onWillDismiss();

    if (data && data.saved) {
      this.hasChanges = true;
      this.loadDailyActivity();
      this.loadHighlightedDates(); // Refresh highlights
    }
  }


  get totalDayAmount(): number {
    return this.dailyWorkers.reduce((acc, item) => acc + (Number(item.monto) || 0) + (Number(item.extra) || 0), 0);
  }

  async editActivity(item: any) {
    const modal = await this.modalCtrl.create({
      component: EditActivityComponent,
      componentProps: {
        data: item
      }
    });

    await modal.present();
    const { data } = await modal.onWillDismiss();

    if (data) {
      if (data.delete) {
        this.performDeletion(item);
      } else {
        this.updateActivity(item, data);
      }
    }
  }

  async performDeletion(item: any) {
    try {
      const spPayload = this.metodosGenService.setModeloGeneral(
        "DELETE", "DELETE", String(item.id), "", "", "", "", "", "", "", "", "", "", "", "", "", ""
      );

      const res = await firstValueFrom(this.javapiService.savePlanillaActividad(spPayload));

      if (res && res.success) {
        this.hasChanges = true;
        this.metodosUi.showToast('Registro eliminado', 'success');
        this.loadDailyActivity();
        this.loadHighlightedDates(); // Refresh highlights
      } else {
        this.metodosUi.showToast('Error al eliminar', 'danger');
      }
    } catch (e) {
      console.error(e);
      this.metodosUi.showToast('Error de conexión', 'danger');
    }
  }

  async updateActivity(originalItem: any, newData: any) {
    try {
      const targetFecha = newData.fecha || originalItem.fecha;
      const targetTurno = newData.turno || originalItem.turno;
      
      // Comprobar si ya existe un registro del mismo trabajador en el mismo turno y fecha
      // (excluyendo el propio registro que estamos actualizando mediante su id)
      const yaExiste = this.dailyWorkers.some(
        w => String(w.id) !== String(originalItem.id) &&
             String(w.trabajador_id) === String(originalItem.trabajador_id) &&
             String(w.fecha) === String(targetFecha) &&
             String(w.turno) === String(targetTurno)
      );

      if (yaExiste) {
        const turnoTxt = targetTurno === 'D' ? 'Diurno' : 'Nocturno';
        await this.metodosUi.showAlert(
          'Duplicado Detectado',
          `El trabajador ${originalItem.nombre} ya tiene registrado planilla para este día en el turno ${turnoTxt}.`
        );
        return;
      }

      // Params: method, subrutina, id, trabajo_id, trabajador_id, fecha, turno, monto, extra, descuento, comentario
      const spPayload = this.metodosGenService.setModeloGeneral(
        "Planilla", "UPDATE", String(originalItem.id), String(this.trabajo?.id), String(originalItem.trabajador_id),
        targetFecha,
        targetTurno,
        String(newData.monto),
        String(newData.extra || 0),
        String(newData.descuento || 0),
        newData.comentario || "", // dt9
        newData.subcontrato ? 'S' : 'N', // dt10
        "", "", "", "", ""
      );

      const res = await firstValueFrom(this.javapiService.savePlanillaActividad(spPayload));

      if (res && res.success) {
        this.hasChanges = true;
        this.metodosUi.showToast('Actualizado correctamente', 'success');
        this.loadDailyActivity();
        this.loadHighlightedDates(); // Refresh highlights in case date changed
      } else {
        this.metodosUi.showToast('Error al actualizar', 'danger');
      }
    } catch (e) {
      console.error(e);
      this.metodosUi.showToast('Error de conexión', 'danger');
    }
  }

}

// Force recompile
